import { z } from "zod";
import { BillingInvoiceSchema, BillingReasonSchema } from "./billing-schemas";
import { OpaqueIdSchema } from "./schemas";

/**
 * Requests that change something already in Stripe (ADR-0073): who it emails, invoices paid or
 * closed some other way, and when a subscription next bills. Each carries the reason the audit
 * trail shows.
 */

export const BillingPaymentMethodSchema = BillingInvoiceSchema.shape.paymentMethod.unwrap();
export type BillingPaymentMethod = z.infer<typeof BillingPaymentMethodSchema>;

/** Where Stripe emails a linked customer's invoices and receipts. */
export const BillingCustomerEmailSchema = z.strictObject({
  billingEmail: z.email().max(320),
  reason: BillingReasonSchema,
});

/** A Stripe invoice the customer paid some other way, such as a check, on the day it arrived. */
export const BillingStripeInvoicePaidOutsideSchema = z.strictObject({
  paidAt: z.number().int().positive(),
  method: BillingPaymentMethodSchema,
  reason: BillingReasonSchema,
});

/** A Stripe invoice that will not be paid: sent in error (void) or written off (uncollectible). */
export const BillingStripeInvoiceCloseSchema = z.strictObject({
  outcome: z.enum(["void", "uncollectible"]),
  reason: BillingReasonSchema,
});

/**
 * Move the day a Stripe subscription next invoices, as when go-live moves. Nothing is charged or
 * credited for the change; the period already paid simply runs to the new day.
 */
export const BillingSubscriptionRenewalSchema = z.strictObject({
  nextInvoiceAt: z.number().int().positive(),
  reason: BillingReasonSchema,
});

/** Apply one credit line of approved terms, through Stripe or recorded from elsewhere. */
export const BillingStripeCreditCreateSchema = z.strictObject({
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema,
  reason: BillingReasonSchema,
});
